import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, Briefcase, Truck, Users, TrendingUp } from 'lucide-react';
import api from '../lib/api';

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MetricsTab = () => {
  const [metrics, setMetrics] = useState([]);

  useEffect(() => {
    api.admin.getStaffMetrics().then(r => setMetrics(r.data)).catch(() => {});
  }, []);

  if (metrics.length === 0) {
    return (
      <div className="text-center py-16 text-white/40" data-testid="metrics-tab">
        <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="text-lg font-medium mb-2">No Metrics Available Yet</p>
        <p className="text-sm">Create jobs and assign estimators to see performance metrics here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="metrics-tab">
      <h2 className="text-lg font-bold text-white">Staff Performance Metrics</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-secondary-dark p-5 rounded-lg border border-white/10">
          <Briefcase className="w-6 h-6 text-electric-teal mb-2" />
          <p className="text-2xl font-bold text-white">{metrics.reduce((s, m) => s + m.total_jobs, 0)}</p>
          <p className="text-white/50 text-sm">Total Jobs</p>
        </div>
        <div className="bg-secondary-dark p-5 rounded-lg border border-white/10">
          <DollarSign className="w-6 h-6 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{fmt(metrics.reduce((s, m) => s + m.total_revenue, 0))}</p>
          <p className="text-white/50 text-sm">Total Revenue</p>
        </div>
        <div className="bg-secondary-dark p-5 rounded-lg border border-white/10">
          <Clock className="w-6 h-6 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{metrics.reduce((s, m) => s + m.total_hours_worked, 0).toFixed(1)}h</p>
          <p className="text-white/50 text-sm">Total Hours Worked</p>
        </div>
        <div className="bg-secondary-dark p-5 rounded-lg border border-white/10">
          <Truck className="w-6 h-6 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{metrics.reduce((s, m) => s + m.total_travel_hours, 0).toFixed(1)}h</p>
          <p className="text-white/50 text-sm">Total Travel Time</p>
        </div>
      </div>

      {/* Per-Staff Metrics */}
      <div className="grid gap-6 lg:grid-cols-2">
        {metrics.map((m) => (
          <div key={m.staff_id} className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden"
            data-testid={`metric-card-${m.staff_id}`}>
            {/* Header */}
            <div className="p-5 border-b border-white/10" style={{ borderTopColor: m.color, borderTopWidth: '3px' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: m.color }}>
                  {m.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <h3 className="text-white font-bold">{m.name}</h3>
                  <span className="text-white/40 text-xs uppercase font-semibold">{m.role}</span>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-electric-teal font-bold text-xl">{m.total_jobs}</p>
                  <p className="text-white/40 text-xs">jobs</p>
                </div>
              </div>
            </div>
            {/* Stats Grid */}
            <div className="p-5 grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/40 text-xs mb-1">Hours Worked</p>
                <p className="text-white font-semibold">{m.total_hours_worked.toFixed(1)} hrs</p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Travel Time</p>
                <p className="text-white font-semibold">{m.total_travel_hours.toFixed(1)} hrs</p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Residential</p>
                <p className="text-white font-semibold">{m.residential_count} <span className="text-white/30 text-xs">jobs</span></p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Commercial</p>
                <p className="text-white font-semibold">{m.commercial_count} <span className="text-white/30 text-xs">jobs</span></p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Total Revenue</p>
                <p className="text-green-400 font-semibold">{fmt(m.total_revenue)}</p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Unpaid</p>
                <p className={`font-semibold ${m.total_unpaid > 0 ? 'text-red-400' : 'text-white/30'}`}>{fmt(m.total_unpaid)}</p>
              </div>
            </div>
            {/* Service Breakdown */}
            {Object.keys(m.service_breakdown).length > 0 && (
              <div className="px-5 pb-4">
                <p className="text-white/40 text-xs mb-2">Services Requested</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(m.service_breakdown).map(([svc, count]) => (
                    <span key={svc} className="bg-white/5 px-2 py-1 rounded text-xs text-white/70">
                      {svc} <span className="text-electric-teal font-bold ml-1">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Status Breakdown */}
            {Object.keys(m.status_breakdown).length > 0 && (
              <div className="px-5 pb-5">
                <p className="text-white/40 text-xs mb-2">Job Status Breakdown</p>
                <div className="flex gap-1">
                  {Object.entries(m.status_breakdown).map(([status, count]) => {
                    const colors = {
                      estimate: '#EAB308', approved: '#3B82F6', in_progress: '#8B5CF6',
                      completed: '#22C55E', invoiced: '#F97316', paid: '#00D9CC', cancelled: '#EF4444'
                    };
                    const total = Object.values(m.status_breakdown).reduce((s, v) => s + v, 0);
                    const pct = (count / total * 100);
                    return (
                      <div key={status} className="rounded-sm h-6 flex items-center justify-center text-[10px] font-bold text-white/90 min-w-[24px]"
                        style={{ backgroundColor: colors[status] || '#666', width: `${Math.max(pct, 10)}%` }}
                        title={`${status}: ${count}`}>
                        {count}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricsTab;
